import { useState } from 'react'
import { Link } from 'react-router-dom'
import { playScanSound } from '../../services/sounds'
import { hapticLight, hapticSuccess } from '../../utils/haptic'

const MEASURE_QUESTIONS = [
  { id: 'pencil', question: 'How long is the pencil?', emoji: '✏️', answer: 5, unit: 'blocks' },
  { id: 'hammer', question: 'How long is the hammer?', emoji: '🔨', answer: 6, unit: 'blocks' },
  { id: 'nail', question: 'How long is the nail?', emoji: '🔩', answer: 3, unit: 'blocks' },
] as const

const BLOCK_OPTIONS = [2, 3, 4, 5, 6, 7] as const

export function MeasureView() {
  const [questionIndex, setQuestionIndex] = useState(0)
  const [selectedBlock, setSelectedBlock] = useState<number | null>(null)
  const [showResult, setShowResult] = useState(false)

  const safeIndex = Math.min(Math.max(0, questionIndex), MEASURE_QUESTIONS.length - 1)
  const q = MEASURE_QUESTIONS[safeIndex]
  if (!q) return null
  const isCorrect = selectedBlock !== null && selectedBlock === q.answer
  const isLastQuestion = questionIndex === MEASURE_QUESTIONS.length - 1

  const handlePickBlock = (n: number) => {
    if (showResult) return
    hapticLight()
    playScanSound()
    setSelectedBlock(n)
    setShowResult(true)
    if (isCorrect) hapticSuccess()
  }

  const handleNext = () => {
    hapticLight()
    if (isLastQuestion) return
    setQuestionIndex((i) => i + 1)
    setSelectedBlock(null)
    setShowResult(false)
  }

  return (
    <div className="page page--measure">
      <header className="measure-header">
        <Link to="/hardware" className="btn-back" aria-label="Back to hardware store">←</Link>
        <h1 className="page-title">📏 Measure it!</h1>
      </header>

      <p className="measure-question">{q.question}</p>
      <div className="measure-ruler" aria-hidden>
        <span className="measure-emoji">{q.emoji}</span>
        <span className="measure-ruler-blocks">
          {BLOCK_OPTIONS.map((n) => (
            <span
              key={n}
              className={`measure-block ${selectedBlock === n ? 'measure-block--selected' : ''} ${showResult && n === q.answer ? 'measure-block--correct' : ''}`}
              aria-hidden
            >
              {n}
            </span>
          ))}
        </span>
      </div>

      <p className="measure-prompt">Tap how many blocks long it is:</p>
      <div className="measure-options" aria-label="Choose length">
        {BLOCK_OPTIONS.map((n) => (
          <button
            key={n}
            type="button"
            className={`btn-large measure-option-btn ${selectedBlock === n ? 'measure-option-btn--selected' : ''} ${showResult && n === q.answer ? 'measure-option-btn--correct' : ''} ${showResult && selectedBlock !== null && n !== q.answer && selectedBlock === n ? 'measure-option-btn--wrong' : ''}`}
            onClick={() => handlePickBlock(n)}
            disabled={showResult}
            aria-pressed={selectedBlock === n}
            aria-label={`${n} blocks`}
          >
            {n} blocks
          </button>
        ))}
      </div>

      {showResult && (
        <div className="measure-result" aria-live="polite">
          <p className="measure-result-text">
            {isCorrect ? (
              <>Correct! The {q.emoji} is {q.answer} {q.unit} long. Great measuring!</>
            ) : (
              <>Not quite – it’s {q.answer} {q.unit} long. Try the next one!</>
            )}
          </p>
          {isLastQuestion ? (
            <div className="measure-done-actions">
              <h2 className="measure-done-title">You’re a great measurer! 📐</h2>
              <Link to="/hardware" className="btn-large btn-primary" style={{ textDecoration: 'none' }}>
                Back to Hardware Store
              </Link>
              <button
                type="button"
                className="btn-large btn-secondary"
                onClick={() => { setQuestionIndex(0); setSelectedBlock(null); setShowResult(false); }}
                style={{ textDecoration: 'none' }}
              >
                Measure again
              </button>
            </div>
          ) : (
            <button type="button" className="btn-large btn-primary" onClick={handleNext} style={{ marginTop: 16 }}>
              Next question
            </button>
          )}
        </div>
      )}

      {!showResult && (
        <Link to="/hardware" className="btn-large btn-secondary" style={{ textDecoration: 'none', marginTop: 24 }}>
          Back to Hardware Store
        </Link>
      )}
    </div>
  )
}
